// Generated from doskai-contracts/contracts/policies/reporting-policy.ts. Do not edit; run reporting-policy:sync.
// Canonical, portable reporting decisions. No database access, provider calls or writes.
// Runtime adoption is explicit: an adapter must prove support before a plan can run.
export type ReportPeriod =
  | 'today'
  | 'yesterday'
  | 'last7'
  | 'last30'
  | 'last90'
  | 'thisMonth'
  | 'lastMonth'
  | 'thisYear'
  | 'custom'
  | 'all'
  | 'asOf';
export type ReportTimeBasis = 'event' | 'cohort' | 'current_state';
export type ReportDimension =
  | 'payingPartyCountry'
  | 'sellerCountry'
  | 'advertiserCountry'
  | 'audienceCountry'
  | 'currencyCode'
  | 'status'
  | 'campaignType'
  | 'audienceSegment'
  | 'day'
  | 'week'
  | 'month';
export type ReportMetricKind = 'count' | 'unique_count' | 'current_count' | 'sum' | 'average' | 'ratio';
export interface ReportMetricDefinition {
  id: string;
  label: string;
  description: string;
  kind: ReportMetricKind;
  unit: 'count' | 'money_minor' | 'percent';
  adapter: string;
  timeBasis: ReportTimeBasis;
  dateField: string | null;
  field?: string;
  distinctField?: string;
  numerator?: string;
  denominator?: string;
  evidence: string;
  allowNegative?: boolean;
}
export interface ReportDefinition {
  id: string;
  version: 1;
  label: string;
  adapter: string;
  permission: string;
  metrics: readonly string[];
  defaultPeriod: ReportPeriod;
  countryDimension: ReportDimension | null;
  dimensions: readonly ReportDimension[];
}
export interface ReportRequest {
  period?: ReportPeriod;
  startDate?: string;
  endDate?: string;
  timezone?: 'UTC';
  countries?: string[];
  countryDimension?: ReportDimension;
  currencies?: string[];
  currencyMode?: 'original';
  groupBy?: ReportDimension[];
}
export interface ReportWindow {
  preset: ReportPeriod;
  timezone: 'UTC';
  startInclusive: number | null;
  endExclusive: number;
  asOf: number;
}
// Supplied by the trusted Server adapter, never accepted as request permissions.
export interface ReportAdapterCapabilities {
  id: string;
  metricIds: readonly string[];
  dimensions: readonly ReportDimension[];
  dateFields: readonly string[];
  periods: readonly ReportPeriod[];
  countryCodes: readonly string[];
  currencyCodes: readonly string[];
}
export interface ReportPlan {
  policyVersion: 1;
  reportId: string;
  definitionVersion: 1;
  adapter: string;
  requiredPermission: string;
  metricIds: readonly string[];
  timeBasis: ReportTimeBasis;
  window: ReportWindow;
  filters: {
    countries: string[];
    countryDimension: ReportDimension | null;
    currencies: string[];
    currencyMode: 'original';
  };
  groupBy: ReportDimension[];
  qaPolicy: 'exclude_confirmed_only';
}
export type MetricValue = {
  availability: 'available' | 'partial' | 'unavailable';
  value?: number | null;
  valuesByCurrency?: Record<string, number>;
  missingRecords: number;
};
export interface ReportResultInput {
  executionId: string;
  generatedAt: number;
  sourceWatermark: number | null;
  coverage: 'complete' | 'partial' | 'unavailable';
  summaries: Record<string, MetricValue>;
  matchedRows: number;
  returnedRows: number;
  nextCursor: string | null;
  excludedConfirmedQA: number;
}
function freeze<T>(value: T): Readonly<T> {
  if (value && typeof value === 'object') {
    for (const child of Object.values(value)) freeze(child);
    Object.freeze(value);
  }
  return value;
}
export const REPORTING_POLICY = freeze({
  version: 1 as const,
  defaultPeriod: 'last30' as ReportPeriod,
  timezone: 'UTC' as const,
  periods: [
    'today',
    'yesterday',
    'last7',
    'last30',
    'last90',
    'thisMonth',
    'lastMonth',
    'thisYear',
    'custom',
    'all',
    'asOf',
  ] as ReportPeriod[],
  interval: 'start_inclusive_end_exclusive',
  rollingCalendarPeriodsIncludeToday: true,
  weekStartsOn: 'monday',
  maxSelectedCodes: 100,
  maxGroupings: 3,
  unknownCountry: 'UNKNOWN',
  unknownCurrency: 'UNKNOWN',
  qaPolicy: 'exclude_confirmed_only' as const,
  currencyMode: 'original' as const,
  missingAmount: 'unavailable_never_zero',
  reportingCurrencyConversion: 'unsupported_in_v1',
  queryAuthority: 'server',
  totalsSource: 'complete_server_aggregation_not_visible_rows',
  stateCountsUse: 'asOf_not_activity_period',
});
export const REPORTING_COUNTRY_DIMENSIONS = freeze({
  payingPartyCountry:
    'Billing/payment country captured with the original transaction, not the current profile or currency.',
  sellerCountry: 'Seller country captured with the business transaction.',
  advertiserCountry: 'Advertiser billing country captured with campaign purchase.',
  audienceCountry:
    'Country recorded for the engagement event; unknown when unavailable. Never substitute advertiser country.',
});

// Normalized field names below describe adapter outputs, not new persisted writes.
// Extending an existing adapter/operator can be configuration-only. New source facts
// still need an explicitly tested adapter/projection and any required indexes.
/** Current-state membership counts; no payment/revenue meaning or historical ranges. */
export const ACCOUNT_STATE_COUNT_REPORTS = freeze({
  subscriptions: {
    reportId: 'subscriptions.current',
    label: 'Current subscriber records',
    metrics: {
      totalSubscriptions: 'All eligible subscriber records',
      activeCount: 'Records with active subscription status',
      proCount: 'Pro tier records, regardless of payment status',
      basicCount: 'Free or Basic tier records',
      cancelledCount: 'Records with cancelled status',
      pastDueCount: 'Records with past-due status',
      monthlyCount: 'Monthly billing-cycle records',
      annualCount: 'Annual billing-cycle records',
      complimentaryCount: 'Complimentary subscription records',
      effectiveProCount: 'Effective Pro access, including Admin trial preview',
    },
  },
  referrals: {
    reportId: 'referrals.current',
    label: 'Current referral records',
    metrics: {
      totalCodes: 'Issued referral-code index records',
      attributedUsers: 'Accounts with a recorded referrer code',
      rewardedUsers: 'Accounts with at least one recorded referral reward',
      pendingRewardUsers: 'Attributed accounts with no recorded rewards',
    },
  },
});
const accountStateMetrics = Object.fromEntries(
  Object.entries(ACCOUNT_STATE_COUNT_REPORTS).flatMap(([domain, definition]) =>
    Object.entries(definition.metrics).map(([field, label]) => [
      `${domain}.${field}`,
      {
        id: `${domain}.${field}`,
        label,
        description: `${label}; complete current source snapshots, confirmed QA excluded.`,
        kind: 'current_count' as const,
        unit: 'count' as const,
        adapter: 'accountCurrentState',
        timeBasis: 'current_state' as const,
        dateField: null,
        distinctField: 'subjectId',
        evidence: 'canonical_account_or_code_snapshot',
      },
    ])
  )
);
const accountStateReports = Object.fromEntries(
  Object.entries(ACCOUNT_STATE_COUNT_REPORTS).map(([domain, definition]) => [
    definition.reportId,
    {
      id: definition.reportId,
      version: 1 as const,
      label: definition.label,
      adapter: 'accountCurrentState',
      permission: 'finance_reporting',
      metrics: Object.keys(definition.metrics).map((field) => `${domain}.${field}`),
      defaultPeriod: 'asOf' as const,
      countryDimension: null,
      dimensions: [],
    },
  ])
);

export const REPORT_METRICS: Readonly<Record<string, ReportMetricDefinition>> = freeze({
  ...accountStateMetrics,
  'payments.cohortRecords': {
    id: 'payments.cohortRecords',
    label: 'Marketplace records',
    description: 'Complete booking-creation cohort across Ads, Collabs and all Store orders; confirmed QA excluded.',
    kind: 'count',
    unit: 'count',
    adapter: 'marketplacePaymentCreationCohort',
    timeBasis: 'cohort',
    dateField: 'bookingCreatedAt',
    evidence: 'canonical_records_confirmed_qa_excluded',
  },
  'payments.cohortCompletedGross': {
    id: 'payments.cohortCompletedGross',
    label: 'Completed gross charges',
    description:
      'Verified ad purchases and completed Collab/Store charges by booking creation date; original currencies, before refunds, fees and payouts. Not platform income.',
    kind: 'sum',
    unit: 'money_minor',
    adapter: 'marketplacePaymentCreationCohort',
    timeBasis: 'cohort',
    dateField: 'bookingCreatedAt',
    field: 'amountMinor',
    evidence: 'verified_original_capture_and_canonical_completion',
  },
  'orders.count': {
    id: 'orders.count',
    label: 'orders count',
    description:
      'Creation cohort; confirmed QA excluded; revenue uses verified capture and canonical completion rules, before refunds.',
    kind: 'count',
    unit: 'count',
    adapter: 'ordersCreationCohort',
    timeBasis: 'cohort',
    dateField: 'ordersCreatedAt',
    evidence: 'verified_original_payment',
  },
  'orders.revenue': {
    id: 'orders.revenue',
    label: 'orders revenue',
    description:
      'Creation cohort; confirmed QA excluded; revenue uses verified capture and canonical completion rules, before refunds.',
    kind: 'sum',
    unit: 'money_minor',
    adapter: 'ordersCreationCohort',
    timeBasis: 'cohort',
    dateField: 'ordersCreatedAt',
    evidence: 'verified_original_payment',
    field: 'amountMinor',
  },
  'orders.averageRevenue': {
    id: 'orders.averageRevenue',
    label: 'orders averageRevenue',
    description:
      'Creation cohort; confirmed QA excluded; revenue uses verified capture and canonical completion rules, before refunds.',
    kind: 'average',
    unit: 'money_minor',
    adapter: 'ordersCreationCohort',
    timeBasis: 'cohort',
    dateField: 'ordersCreatedAt',
    evidence: 'verified_original_payment',
    field: 'amountMinor',
  },
  'rentals.count': {
    id: 'rentals.count',
    label: 'rentals count',
    description:
      'Creation cohort; confirmed QA excluded; revenue uses verified capture and canonical completion rules, before refunds.',
    kind: 'count',
    unit: 'count',
    adapter: 'rentalsCreationCohort',
    timeBasis: 'cohort',
    dateField: 'rentalsCreatedAt',
    evidence: 'verified_original_payment',
  },
  'rentals.revenue': {
    id: 'rentals.revenue',
    label: 'rentals revenue',
    description:
      'Creation cohort; confirmed QA excluded; revenue uses verified capture and canonical completion rules, before refunds.',
    kind: 'sum',
    unit: 'money_minor',
    adapter: 'rentalsCreationCohort',
    timeBasis: 'cohort',
    dateField: 'rentalsCreatedAt',
    evidence: 'verified_original_payment',
    field: 'amountMinor',
  },
  'rentals.averageRevenue': {
    id: 'rentals.averageRevenue',
    label: 'rentals averageRevenue',
    description:
      'Creation cohort; confirmed QA excluded; revenue uses verified capture and canonical completion rules, before refunds.',
    kind: 'average',
    unit: 'money_minor',
    adapter: 'rentalsCreationCohort',
    timeBasis: 'cohort',
    dateField: 'rentalsCreatedAt',
    evidence: 'verified_original_payment',
    field: 'amountMinor',
  },
  'collabs.count': {
    id: 'collabs.count',
    label: 'Collabs in cohort',
    description: 'Creation cohort; confirmed QA excluded; money requires original capture evidence, before refunds.',
    kind: 'count',
    unit: 'count',
    adapter: 'collabCreationCohort',
    timeBasis: 'cohort',
    dateField: 'collabCreatedAt',
    evidence: 'verified_original_payment',
  },
  'collabs.grossCaptured': {
    id: 'collabs.grossCaptured',
    label: 'Gross captured value',
    description: 'Creation cohort; confirmed QA excluded; money requires original capture evidence, before refunds.',
    kind: 'sum',
    unit: 'money_minor',
    adapter: 'collabCreationCohort',
    timeBasis: 'cohort',
    dateField: 'collabCreatedAt',
    evidence: 'verified_original_payment',
    field: 'amountMinor',
  },
  'collabs.averageCaptured': {
    id: 'collabs.averageCaptured',
    label: 'Average captured value',
    description: 'Creation cohort; confirmed QA excluded; money requires original capture evidence, before refunds.',
    kind: 'average',
    unit: 'money_minor',
    adapter: 'collabCreationCohort',
    timeBasis: 'cohort',
    dateField: 'collabCreatedAt',
    evidence: 'verified_original_payment',
    field: 'amountMinor',
  },
  'ads.campaigns': {
    id: 'ads.campaigns',
    label: 'Campaigns created',
    description: 'Eligible campaigns created in the selected period.',
    kind: 'count',
    unit: 'count',
    adapter: 'adCampaignCohort',
    timeBasis: 'cohort',
    dateField: 'campaignCreatedAt',
    evidence: 'canonical_campaign_confirmed_qa_excluded',
  },
  'ads.impressionsLifetime': {
    id: 'ads.impressionsLifetime',
    label: 'Lifetime impressions',
    description: 'Lifetime impressions of the selected campaign creation cohort.',
    kind: 'sum',
    unit: 'count',
    field: 'impressions',
    adapter: 'adCampaignCohort',
    timeBasis: 'cohort',
    dateField: 'campaignCreatedAt',
    evidence: 'canonical_delivery_counters',
  },
  'ads.clicksLifetime': {
    id: 'ads.clicksLifetime',
    label: 'Lifetime clicks',
    description: 'Lifetime clicks of the selected campaign creation cohort.',
    kind: 'sum',
    unit: 'count',
    field: 'clicks',
    adapter: 'adCampaignCohort',
    timeBasis: 'cohort',
    dateField: 'campaignCreatedAt',
    evidence: 'canonical_delivery_counters',
  },
  'ads.spendLifetime': {
    id: 'ads.spendLifetime',
    label: 'Lifetime consumed spend',
    description:
      'Consumed delivery spend, not the original campaign purchase charge. Missing verification remains unavailable.',
    kind: 'sum',
    unit: 'money_minor',
    field: 'verifiedSpendMinor',
    adapter: 'adCampaignCohort',
    timeBasis: 'cohort',
    dateField: 'campaignCreatedAt',
    evidence: 'verified_payment_and_delivery_spend',
  },
  'ads.averageSpendLifetime': {
    id: 'ads.averageSpendLifetime',
    label: 'Average consumed spend',
    description:
      'Average of known verified spend per eligible campaign, separately by currency. Exclude unknown amounts from the denominator.',
    kind: 'average',
    unit: 'money_minor',
    field: 'verifiedSpendMinor',
    adapter: 'adCampaignCohort',
    timeBasis: 'cohort',
    dateField: 'campaignCreatedAt',
    evidence: 'verified_payment_and_delivery_spend',
  },
  'ads.ctrLifetime': {
    id: 'ads.ctrLifetime',
    label: 'Lifetime CTR',
    description:
      'Total cohort clicks divided by total cohort impressions times 100; never average campaign percentages.',
    kind: 'ratio',
    unit: 'percent',
    numerator: 'ads.clicksLifetime',
    denominator: 'ads.impressionsLifetime',
    adapter: 'adCampaignCohort',
    timeBasis: 'cohort',
    dateField: 'campaignCreatedAt',
    evidence: 'canonical_delivery_counters',
  },
  'ads.spendActivity': {
    id: 'ads.spendActivity',
    label: 'Spend in period',
    description: 'Actual delivery consumption during the selected activity period, in campaign currency.',
    kind: 'sum',
    unit: 'money_minor',
    field: 'verifiedSpendMinor',
    adapter: 'adDeliveryActivity',
    timeBasis: 'event',
    dateField: 'occurredAt',
    evidence: 'verified_payment_and_delivery_events_or_daily_buckets',
  },
  'payments.capturedCount': {
    id: 'payments.capturedCount',
    label: 'Captured payments',
    description: 'Unique verified successful provider captures; count each capture once.',
    kind: 'unique_count',
    unit: 'count',
    distinctField: 'providerCaptureId',
    adapter: 'verifiedCaptures',
    timeBasis: 'event',
    dateField: 'capturedAt',
    evidence: 'original_provider_capture',
  },
  'payments.capturedGross': {
    id: 'payments.capturedGross',
    label: 'Gross captured amount',
    description:
      'Verified captured amount including charged fees/tax, before refunds; not platform revenue or seller earnings.',
    kind: 'sum',
    unit: 'money_minor',
    field: 'capturedMinor',
    adapter: 'verifiedCaptures',
    timeBasis: 'event',
    dateField: 'capturedAt',
    evidence: 'original_provider_capture_deduplicated',
  },
  'refunds.completedCount': {
    id: 'refunds.completedCount',
    label: 'Completed refunds',
    description: 'Unique successful financial refunds, excluding return/inspection/status-only events.',
    kind: 'unique_count',
    unit: 'count',
    distinctField: 'providerRefundId',
    adapter: 'confirmedRefunds',
    timeBasis: 'event',
    dateField: 'refundCompletedAt',
    evidence: 'canonical_successful_refund_receipt',
  },
  'refunds.completedAmount': {
    id: 'refunds.completedAmount',
    label: 'Completed refund amount',
    description: 'Successful refund amounts by refund completion date and refund currency.',
    kind: 'sum',
    unit: 'money_minor',
    field: 'refundedMinor',
    adapter: 'confirmedRefunds',
    timeBasis: 'event',
    dateField: 'refundCompletedAt',
    evidence: 'canonical_successful_refund_receipt_deduplicated',
  },
  'engagement.currentLikes': {
    id: 'engagement.currentLikes',
    label: 'Current likes',
    description: 'Current distinct like relationships as of the cutoff, not likes added during a period.',
    kind: 'current_count',
    unit: 'count',
    distinctField: 'actorId',
    adapter: 'engagementRelationships',
    timeBasis: 'current_state',
    dateField: null,
    evidence: 'canonical_relationships',
  },
  'engagement.currentSaves': {
    id: 'engagement.currentSaves',
    label: 'Current saves',
    description: 'Current distinct save relationships as of the cutoff, not historical save actions.',
    kind: 'current_count',
    unit: 'count',
    distinctField: 'actorId',
    adapter: 'engagementRelationships',
    timeBasis: 'current_state',
    dateField: null,
    evidence: 'canonical_relationships',
  },
  'engagement.currentSharers': {
    id: 'engagement.currentSharers',
    label: 'Current unique sharers',
    description:
      'Distinct people represented by current share relationships. Never label this total share actions or recipients.',
    kind: 'current_count',
    unit: 'count',
    distinctField: 'actorId',
    adapter: 'engagementRelationships',
    timeBasis: 'current_state',
    dateField: null,
    evidence: 'canonical_relationships_not_increment_only_counter',
  },
  'engagement.shareActions': {
    id: 'engagement.shareActions',
    label: 'Share actions in period',
    description:
      'Unique successfully recorded share actions; repeated actions by one person count separately. Recipient count is a different metric.',
    kind: 'unique_count',
    unit: 'count',
    distinctField: 'eventId',
    adapter: 'engagementEvents',
    timeBasis: 'event',
    dateField: 'occurredAt',
    evidence: 'deduplicated_successful_share_action_events',
  },
});
export const REPORT_DEFINITIONS: Readonly<Record<string, ReportDefinition>> = freeze({
  ...accountStateReports,
  'payments.creationCohort': {
    id: 'payments.creationCohort',
    version: 1,
    label: 'Marketplace gross charges by creation cohort',
    adapter: 'marketplacePaymentCreationCohort',
    permission: 'financial_analytics',
    metrics: ['payments.cohortRecords', 'payments.cohortCompletedGross'],
    defaultPeriod: 'last30',
    countryDimension: null,
    dimensions: ['currencyCode'],
  },
  'orders.creationCohort': {
    id: 'orders.creationCohort',
    version: 1,
    label: 'orders creation cohort',
    adapter: 'ordersCreationCohort',
    permission: 'financial_analytics',
    metrics: ['orders.count', 'orders.revenue', 'orders.averageRevenue'],
    defaultPeriod: 'last30',
    countryDimension: null,
    dimensions: ['currencyCode', 'status'],
  },
  'rentals.creationCohort': {
    id: 'rentals.creationCohort',
    version: 1,
    label: 'rentals creation cohort',
    adapter: 'rentalsCreationCohort',
    permission: 'financial_analytics',
    metrics: ['rentals.count', 'rentals.revenue', 'rentals.averageRevenue'],
    defaultPeriod: 'last30',
    countryDimension: null,
    dimensions: ['currencyCode', 'status'],
  },
  'collabs.creationCohort': {
    id: 'collabs.creationCohort',
    version: 1,
    label: 'Collab creation cohort',
    adapter: 'collabCreationCohort',
    permission: 'financial_analytics',
    metrics: ['collabs.count', 'collabs.grossCaptured', 'collabs.averageCaptured'],
    defaultPeriod: 'last30',
    countryDimension: 'payingPartyCountry',
    dimensions: ['currencyCode', 'status', 'payingPartyCountry'],
  },
  'ads.campaignCohort': {
    id: 'ads.campaignCohort',
    version: 1,
    label: 'Campaign creation cohort',
    adapter: 'adCampaignCohort',
    permission: 'marketing_analytics',
    metrics: [
      'ads.campaigns',
      'ads.impressionsLifetime',
      'ads.clicksLifetime',
      'ads.spendLifetime',
      'ads.averageSpendLifetime',
      'ads.ctrLifetime',
    ],
    defaultPeriod: 'last30',
    countryDimension: 'advertiserCountry',
    dimensions: ['advertiserCountry', 'currencyCode', 'status', 'campaignType', 'day', 'week', 'month'],
  },
  'ads.deliveryActivity': {
    id: 'ads.deliveryActivity',
    version: 1,
    label: 'Ad delivery activity',
    adapter: 'adDeliveryActivity',
    permission: 'marketing_analytics',
    metrics: ['ads.spendActivity'],
    defaultPeriod: 'last30',
    countryDimension: 'advertiserCountry',
    dimensions: [
      'advertiserCountry',
      'audienceCountry',
      'audienceSegment',
      'currencyCode',
      'campaignType',
      'day',
      'week',
      'month',
    ],
  },
  'finance.captures': {
    id: 'finance.captures',
    version: 1,
    label: 'Verified captured payments',
    adapter: 'verifiedCaptures',
    permission: 'finance_reporting',
    metrics: ['payments.capturedCount', 'payments.capturedGross'],
    defaultPeriod: 'last30',
    countryDimension: 'payingPartyCountry',
    dimensions: ['payingPartyCountry', 'sellerCountry', 'currencyCode', 'day', 'week', 'month'],
  },
  'finance.refunds': {
    id: 'finance.refunds',
    version: 1,
    label: 'Confirmed financial refunds',
    adapter: 'confirmedRefunds',
    permission: 'finance_reporting',
    metrics: ['refunds.completedCount', 'refunds.completedAmount'],
    defaultPeriod: 'last30',
    countryDimension: 'payingPartyCountry',
    dimensions: ['payingPartyCountry', 'sellerCountry', 'currencyCode', 'day', 'week', 'month'],
  },
  'engagement.current': {
    id: 'engagement.current',
    version: 1,
    label: 'Current engagement relationships',
    adapter: 'engagementRelationships',
    permission: 'engagement_reporting',
    metrics: ['engagement.currentLikes', 'engagement.currentSaves', 'engagement.currentSharers'],
    defaultPeriod: 'asOf',
    countryDimension: null,
    dimensions: [],
  },
  'engagement.shareActivity': {
    id: 'engagement.shareActivity',
    version: 1,
    label: 'Share activity',
    adapter: 'engagementEvents',
    permission: 'engagement_reporting',
    metrics: ['engagement.shareActions'],
    defaultPeriod: 'last30',
    countryDimension: 'audienceCountry',
    dimensions: ['audienceCountry', 'audienceSegment', 'day', 'week', 'month'],
  },
});
function instant(value: number): number {
  if (
    !Number.isSafeInteger(value) ||
    value < 0 ||
    !Number.isFinite(new Date(value).getTime()) ||
    !Number.isSafeInteger(value + 1)
  )
    throw new Error('Invalid reporting cutoff');
  return value;
}
/** Read-only money admission. Missing/invalid evidence never becomes zero or USD. */
export function reportingMoney(amountMinor: unknown, currencyCode: unknown) {
  const raw = typeof amountMinor === 'string' ? amountMinor.trim() : amountMinor;
  const parsed = typeof raw === 'number' || (typeof raw === 'string' && raw !== '') ? Number(raw) : NaN;
  const code = typeof currencyCode === 'string' ? currencyCode.trim().toUpperCase() : '';
  return {
    amountMinor: Number.isSafeInteger(parsed) ? parsed : null,
    currencyCode: /^[A-Z]{3}$/.test(code) ? code : null,
  };
}

function utcDate(value: unknown): number {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value))
    throw new Error('Use YYYY-MM-DD for reporting dates');
  const parsed = Date.parse(value + 'T00:00:00.000Z');
  if (!Number.isFinite(parsed) || new Date(parsed).toISOString().slice(0, 10) !== value)
    throw new Error('Invalid calendar date');
  return parsed;
}
export function resolveReportWindow(request: ReportRequest = {}, asOf = Date.now()): ReportWindow {
  instant(asOf);
  if (request.timezone !== undefined && request.timezone !== 'UTC') throw new Error('Reporting v1 supports UTC only');
  const preset = request.period === undefined ? REPORTING_POLICY.defaultPeriod : request.period;
  if (!REPORTING_POLICY.periods.includes(preset)) throw new Error('Unknown reporting period; aliases are not accepted');
  if (preset !== 'custom' && (request.startDate !== undefined || request.endDate !== undefined))
    throw new Error('Dates require the custom period');
  const day = 86400000,
    now = new Date(asOf),
    today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  let start: number | null = null,
    end = asOf + 1;
  if (preset === 'today') start = today;
  else if (preset === 'yesterday') {
    start = today - day;
    end = today;
  } else if (preset === 'last7' || preset === 'last30' || preset === 'last90')
    start = today - (Number(preset.slice(4)) - 1) * day;
  else if (preset === 'thisMonth') start = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1);
  else if (preset === 'lastMonth') {
    start = Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1);
    end = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1);
  } else if (preset === 'thisYear') start = Date.UTC(now.getUTCFullYear(), 0, 1);
  else if (preset === 'custom') {
    start = utcDate(request.startDate);
    const requestedEnd = utcDate(request.endDate) + day;
    if (requestedEnd <= start) throw new Error('Reporting end must not precede start');
    end = Math.min(requestedEnd, end);
  } else if (preset !== 'all' && preset !== 'asOf') throw new Error('Period resolver is not implemented');
  if (start !== null && (start < 0 || start >= end))
    throw new Error('Reporting period is outside the available cutoff');
  return freeze({
    preset,
    timezone: 'UTC',
    startInclusive: start,
    endExclusive: end,
    asOf,
  });
}
function codes(values: unknown, currency: boolean): string[] {
  if (values === undefined) return [];
  if (!Array.isArray(values) || values.length > REPORTING_POLICY.maxSelectedCodes)
    throw new Error('Reporting filters must be arrays of at most 100 codes');
  const normalized = values.map((v) => {
    if (typeof v !== 'string') throw new Error('Invalid reporting code');
    const code = v.trim().toUpperCase();
    if (code !== 'UNKNOWN' && !(currency ? /^[A-Z]{3}$/ : /^[A-Z]{2}$/).test(code))
      throw new Error('Invalid reporting code format');
    return code;
  });
  return [...new Set(normalized)].sort();
}
export function validateReportingCatalog(
  metrics: Readonly<Record<string, ReportMetricDefinition>> = REPORT_METRICS,
  reports: Readonly<Record<string, ReportDefinition>> = REPORT_DEFINITIONS
): void {
  for (const [id, metric] of Object.entries(metrics)) {
    if (id !== metric.id || !metric.adapter || !metric.label || !metric.description || !metric.evidence)
      throw new Error('Incomplete metric definition: ' + id);
    if (
      !['count', 'unique_count', 'current_count', 'sum', 'average', 'ratio'].includes(metric.kind) ||
      !['count', 'money_minor', 'percent'].includes(metric.unit) ||
      !['event', 'cohort', 'current_state'].includes(metric.timeBasis)
    )
      throw new Error('Unsupported metric definition: ' + id);
    if (
      (metric.timeBasis === 'current_state') !== (metric.dateField === null) ||
      (metric.timeBasis !== 'current_state' && !metric.dateField)
    )
      throw new Error('Metric date basis is ambiguous: ' + id);
    if (
      (['count', 'unique_count', 'current_count'].includes(metric.kind) && metric.unit !== 'count') ||
      (metric.kind === 'ratio' && metric.unit !== 'percent') ||
      (metric.kind === 'current_count') !== (metric.timeBasis === 'current_state')
    )
      throw new Error('Invalid metric unit/kind: ' + id);
    if (['sum', 'average'].includes(metric.kind) && !metric.field) throw new Error('Metric needs a value field: ' + id);
    if (['unique_count', 'current_count'].includes(metric.kind) && !metric.distinctField)
      throw new Error('Metric needs a distinct key: ' + id);
    if (metric.kind === 'ratio') {
      const a = metrics[metric.numerator || ''],
        b = metrics[metric.denominator || ''];
      if (
        !a ||
        !b ||
        !['count', 'sum', 'unique_count', 'current_count'].includes(a.kind) ||
        !['count', 'sum', 'unique_count', 'current_count'].includes(b.kind) ||
        a.unit !== 'count' ||
        b.unit !== 'count' ||
        [a, b].some(
          (m) => m.adapter !== metric.adapter || m.timeBasis !== metric.timeBasis || m.dateField !== metric.dateField
        )
      )
        throw new Error('Ratio inputs must share a population/date basis: ' + id);
    }
  }
  for (const [id, report] of Object.entries(reports)) {
    if (
      id !== report.id ||
      report.version !== 1 ||
      !report.permission ||
      !report.metrics.length ||
      new Set(report.metrics).size !== report.metrics.length
    )
      throw new Error('Invalid report definition: ' + id);
    const members = report.metrics.map((m) => metrics[m]);
    if (
      members.some(
        (m) =>
          !m ||
          m.adapter !== report.adapter ||
          m.timeBasis !== members[0].timeBasis ||
          m.dateField !== members[0].dateField
      )
    )
      throw new Error('Report mixes source/date contracts: ' + id);
    if (
      !REPORTING_POLICY.periods.includes(report.defaultPeriod) ||
      (members[0].timeBasis === 'current_state') !== (report.defaultPeriod === 'asOf')
    )
      throw new Error('Invalid report default period: ' + id);
    if (
      report.countryDimension &&
      (!Object.hasOwn(REPORTING_COUNTRY_DIMENSIONS, report.countryDimension) ||
        !report.dimensions.includes(report.countryDimension))
    )
      throw new Error('Invalid report country dimension: ' + id);
    for (const m of members)
      if (m.kind === 'ratio' && (!report.metrics.includes(m.numerator!) || !report.metrics.includes(m.denominator!)))
        throw new Error('Report omits ratio inputs: ' + id);
  }
}
export function createReportPlan(
  reportId: string,
  request: ReportRequest,
  capabilities: ReportAdapterCapabilities,
  asOf = Date.now()
): ReportPlan {
  validateReportingCatalog();
  if (!Object.hasOwn(REPORT_DEFINITIONS, reportId)) throw new Error('Unknown report');
  const report = REPORT_DEFINITIONS[reportId],
    first = REPORT_METRICS[report.metrics[0]];
  const allowedKeys = [
    'period',
    'startDate',
    'endDate',
    'timezone',
    'countries',
    'countryDimension',
    'currencies',
    'currencyMode',
    'groupBy',
  ];
  if (
    !request ||
    typeof request !== 'object' ||
    Array.isArray(request) ||
    Object.keys(request).some((k) => !allowedKeys.includes(k))
  )
    throw new Error('Unsupported report request field');
  if (!capabilities || capabilities.id !== report.adapter) throw new Error('Reporting adapter is not registered');
  if (report.metrics.some((id) => !capabilities.metricIds.includes(id)))
    throw new Error('Reporting adapter lacks required metrics');
  if (first.dateField && !capabilities.dateFields.includes(first.dateField))
    throw new Error('Reporting adapter lacks the required date field');
  const window = resolveReportWindow(
    {
      ...request,
      period: request.period === undefined ? report.defaultPeriod : request.period,
    },
    asOf
  );
  if (
    !capabilities.periods.includes(window.preset) ||
    (first.timeBasis === 'current_state') !== (window.preset === 'asOf')
  )
    throw new Error('Period is unsupported for this metric basis');
  if (request.currencyMode !== undefined && request.currencyMode !== 'original')
    throw new Error('Currency conversion is unsupported in reporting v1');
  const countries = codes(request.countries, false),
    currencies = codes(request.currencies, true);
  if (
    countries.some((code) => !capabilities.countryCodes.includes(code)) ||
    currencies.some((code) => !capabilities.currencyCodes.includes(code))
  )
    throw new Error('Country/currency code is unsupported by this reporting adapter');
  const countryDimension = request.countryDimension ?? report.countryDimension;
  if (request.countryDimension !== undefined && !Object.hasOwn(REPORTING_COUNTRY_DIMENSIONS, request.countryDimension))
    throw new Error('Unknown country dimension');
  const groupBy = request.groupBy === undefined ? [] : request.groupBy;
  if (
    !Array.isArray(groupBy) ||
    groupBy.length > REPORTING_POLICY.maxGroupings ||
    new Set(groupBy).size !== groupBy.length
  )
    throw new Error('Invalid reporting grouping');
  const required = [
    ...groupBy,
    ...(countries.length || request.countryDimension !== undefined ? [countryDimension] : []),
    ...(currencies.length ? ['currencyCode'] : []),
  ];
  if (
    required.some(
      (d) =>
        !d ||
        !report.dimensions.includes(d as ReportDimension) ||
        !capabilities.dimensions.includes(d as ReportDimension)
    )
  )
    throw new Error('Requested dimension is unsupported; never apply a local fallback');
  return freeze({
    policyVersion: 1,
    reportId,
    definitionVersion: report.version,
    adapter: report.adapter,
    requiredPermission: report.permission,
    metricIds: [...report.metrics],
    timeBasis: first.timeBasis,
    window,
    filters: {
      countries,
      countryDimension,
      currencies,
      currencyMode: 'original',
    },
    groupBy: [...groupBy],
    qaPolicy: REPORTING_POLICY.qaPolicy,
  });
}
export function reportingRatio(numerator: number | null, denominator: number | null, percent = false): number | null {
  if (
    numerator === null ||
    denominator === null ||
    !Number.isSafeInteger(numerator) ||
    !Number.isSafeInteger(denominator) ||
    numerator < 0 ||
    denominator <= 0
  )
    return null;
  return (numerator / denominator) * (percent ? 100 : 1);
}
export function createReportResult(plan: ReportPlan, input: ReportResultInput) {
  if (plan.policyVersion !== REPORTING_POLICY.version || !Object.hasOwn(REPORT_DEFINITIONS, plan.reportId))
    throw new Error('Invalid report plan');
  const definition = REPORT_DEFINITIONS[plan.reportId];
  if (
    plan.definitionVersion !== definition.version ||
    plan.adapter !== definition.adapter ||
    JSON.stringify(plan.metricIds) !== JSON.stringify(definition.metrics)
  )
    throw new Error('Report plan does not match its definition');
  if (!input.executionId?.trim() || !['complete', 'partial', 'unavailable'].includes(input.coverage))
    throw new Error('Invalid report execution');
  instant(input.generatedAt);
  if (
    input.generatedAt < plan.window.asOf ||
    (input.sourceWatermark !== null &&
      (!Number.isSafeInteger(input.sourceWatermark) ||
        input.sourceWatermark < 0 ||
        input.sourceWatermark > plan.window.asOf))
  )
    throw new Error('Invalid report freshness');
  for (const count of [input.matchedRows, input.returnedRows, input.excludedConfirmedQA])
    if (!Number.isSafeInteger(count) || count < 0) throw new Error('Invalid report row counts');
  if (
    input.returnedRows > input.matchedRows ||
    (input.returnedRows < input.matchedRows && !input.nextCursor) ||
    (input.returnedRows === input.matchedRows && input.nextCursor !== null)
  )
    throw new Error('Incomplete report pagination metadata');
  if (Object.keys(input.summaries).length !== plan.metricIds.length)
    throw new Error('Report summaries must match the definition');
  for (const id of plan.metricIds) {
    const metric = REPORT_METRICS[id],
      value = input.summaries[id];
    if (
      !value ||
      !['available', 'partial', 'unavailable'].includes(value.availability) ||
      !Number.isSafeInteger(value.missingRecords) ||
      value.missingRecords < 0 ||
      (value.availability === 'available' && value.missingRecords > 0)
    )
      throw new Error('Invalid metric coverage: ' + id);
    if (metric.unit === 'money_minor') {
      if (value.value !== undefined && value.value !== null)
        throw new Error('Money requires separate currency buckets');
      const buckets = value.valuesByCurrency;
      if (!buckets || typeof buckets !== 'object' || Array.isArray(buckets))
        throw new Error('Missing currency buckets');
      for (const [code, amount] of Object.entries(buckets))
        if (
          !/^[A-Z]{3}$/.test(code) ||
          typeof amount !== 'number' ||
          (!metric.allowNegative && amount < 0) ||
          (plan.filters.currencies.length > 0 && !plan.filters.currencies.includes(code)) ||
          !Number.isFinite(amount) ||
          (metric.kind !== 'average' && !Number.isSafeInteger(amount))
        )
          throw new Error('Invalid currency bucket');
      if (
        value.availability === 'unavailable'
          ? Object.keys(buckets).length > 0
          : Object.keys(buckets).length === 0 && input.matchedRows > 0
      )
        throw new Error('Unavailable money must not become zero');
    } else {
      if (value.valuesByCurrency !== undefined) throw new Error('Non-money metric cannot use money buckets');
      if (
        value.availability === 'unavailable'
          ? value.value !== null
          : typeof value.value !== 'number' ||
            !Number.isFinite(value.value) ||
            value.value < 0 ||
            (metric.unit === 'count' && metric.kind !== 'average' && !Number.isSafeInteger(value.value))
      )
        throw new Error('Unavailable/count metric value is invalid');
    }
  }
  if (
    input.coverage === 'complete' &&
    (input.sourceWatermark !== plan.window.asOf ||
      Object.values(input.summaries).some((v) => v.missingRecords > 0 || v.availability === 'partial'))
  )
    throw new Error('Incomplete metrics cannot claim complete coverage');
  // Totals are supplied by the authorized server adapter, not derived from rows.
  return freeze({
    ...input,
    policyVersion: plan.policyVersion,
    definitionVersion: plan.definitionVersion,
    reportId: plan.reportId,
    effectivePlan: plan,
    summaries: Object.fromEntries(
      Object.entries(input.summaries).map(([id, value]) => [
        id,
        {
          ...value,
          ...(value.valuesByCurrency ? { valuesByCurrency: { ...value.valuesByCurrency } } : {}),
        },
      ])
    ),
  });
}

export function reportingTimeBucket(value: number, granularity: 'day' | 'week' | 'month') {
  instant(value);
  const date = new Date(value),
    day = 86400000;
  let start = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
    end: number;
  if (granularity === 'week') {
    start -= ((date.getUTCDay() + 6) % 7) * day;
    end = start + 7 * day;
  } else if (granularity === 'month') {
    start = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1);
    end = Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 1);
  } else if (granularity === 'day') end = start + day;
  else throw new Error('Unknown reporting granularity');
  return freeze({
    timezone: 'UTC',
    granularity,
    startInclusive: start,
    endExclusive: end,
  });
}
